import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/PagePrimitives";
import { Eyebrow } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import { BRAND_DEFAULTS } from "@/branding/BrandProvider";

interface FormState {
  name: string;
  logoLight: string;
  logoDark: string;
  primary: string;
}

function buildDemoUrl(state: FormState, origin: string): string {
  const params = new URLSearchParams();
  const trimmed: Record<keyof FormState, string> = {
    name: state.name.trim(),
    logoLight: state.logoLight.trim(),
    logoDark: state.logoDark.trim(),
    primary: state.primary.trim(),
  };
  if (trimmed.name) params.set("name", trimmed.name);
  if (trimmed.logoLight) params.set("logoLight", trimmed.logoLight);
  if (trimmed.logoDark) params.set("logoDark", trimmed.logoDark);
  if (trimmed.primary) params.set("primary", trimmed.primary);
  const query = params.toString();
  return `${origin}/demo${query ? `?${query}` : ""}`;
}

export function DemoSetupPage() {
  const [state, setState] = useState<FormState>({
    name: "",
    logoLight: "",
    logoDark: "",
    primary: BRAND_DEFAULTS.primary,
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const demoUrl = useMemo(() => buildDemoUrl(state, origin), [state, origin]);
  const demoPath = useMemo(() => buildDemoUrl(state, ""), [state]);
  const resetUrl = useMemo(() => `${origin}/demo?reset=1`, [origin]);

  const previewName = state.name.trim() || BRAND_DEFAULTS.name;
  const previewLight = state.logoLight.trim() || state.logoDark.trim() || BRAND_DEFAULTS.logoLight;
  const previewDark = state.logoDark.trim() || state.logoLight.trim() || BRAND_DEFAULTS.logoDark;
  const previewPrimary = state.primary.trim() || BRAND_DEFAULTS.primary;

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-6 p-4 sm:p-6">
      <PageHeader
        className="mb-0"
        eyebrow="Sandbox"
        title="Demo setup"
        subtitle="Configure a client's brand for a demo. Opening the generated link sets a cookie in their browser, so the whole app shows their branding until reset."
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Section title="Brand" description={<>Leave blank to use Dolfin defaults.</>} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder={BRAND_DEFAULTS.name}
                value={state.name}
                onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="logoLight">Light logo URL</Label>
              <Input
                id="logoLight"
                placeholder="https://example.com/logo-light.svg"
                value={state.logoLight}
                onChange={(e) => setState((s) => ({ ...s, logoLight: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                If only one logo is supplied, the other falls back to it.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="logoDark">Dark logo URL</Label>
              <Input
                id="logoDark"
                placeholder="https://example.com/logo-dark.svg"
                value={state.logoDark}
                onChange={(e) => setState((s) => ({ ...s, logoDark: e.target.value }))}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="primary">Primary colour</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="primary"
                  className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background"
                  value={state.primary || BRAND_DEFAULTS.primary}
                  onChange={(e) => setState((s) => ({ ...s, primary: e.target.value }))}
                />
                <Input
                  aria-label="Primary colour hex"
                  placeholder={BRAND_DEFAULTS.primary}
                  value={state.primary}
                  onChange={(e) => setState((s) => ({ ...s, primary: e.target.value }))}
                />
              </div>
            </div>
          </Section>

        <Section title="Preview" description={<>What the client will see in the header.</>} className="flex flex-col gap-4">
            <div
              className="flex items-center gap-3 rounded-lg border p-3"
              style={{ background: "#ffffff", color: "#0b1020" }}
            >
              <img src={previewLight} alt={previewName} className="h-7 w-7" />
              <span className="font-semibold">{previewName}</span>
              <span className="ml-auto text-xs text-muted-foreground">Light mode</span>
            </div>
            <div
              className="flex items-center gap-3 rounded-lg border p-3"
              style={{ background: "#030821", color: "#ffffff" }}
            >
              <img src={previewDark} alt={previewName} className="h-7 w-7" />
              <span className="font-semibold">{previewName}</span>
              <span className="ml-auto text-xs opacity-60">Dark mode</span>
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <span
                className="h-7 w-7 rounded-md border"
                style={{ background: previewPrimary }}
              />
              <code className="text-xs">{previewPrimary}</code>
              <span className="ml-auto text-xs text-muted-foreground">Primary</span>
            </div>
          </Section>
      </div>

      <Section title="Demo link" description={<>Send this to the client (or open it yourself). Visiting it stores their brand in a
            cookie for 30 days, then drops them on the home page.</>} className="flex flex-col gap-3">
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-2">
            <code className="flex-1 truncate text-xs">{demoUrl}</code>
            <CopyButton value={demoUrl} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild>
              <Link to={demoPath}>Open demo link</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/demo?reset=1">Reset to Dolfin</Link>
            </Button>
            <span className="ml-auto text-xs text-muted-foreground">
              Reset URL: <code>{resetUrl}</code>
            </span>
          </div>
        </Section>
    </div>
  );
}

function Section({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <Eyebrow>{title}</Eyebrow>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      <div className={cn("mt-4", className)}>{children}</div>
    </section>
  );
}
